import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const LEAD_STATUS_OPTS = ['NEW', 'VIEWED', 'RESPONDED', 'CLOSED_WON', 'CLOSED_LOST'];

const LEAD_BADGE = {
  NEW:          { bg: '#dbeafe', color: '#1e40af' },
  VIEWED:       { bg: '#f3f4f6', color: '#374151' },
  RESPONDED:    { bg: '#dcfce7', color: '#166534' },
  CLOSED_WON:   { bg: '#fef9c3', color: '#854d0e' },
  CLOSED_LOST:  { bg: '#fee2e2', color: '#991b1b' },
};

function ListingEditor({ listing, onSaved }) {
  const { t } = useTranslation();
  const isNew = !listing || listing.exists === false;
  const [form, setForm] = useState({
    bio:             listing?.bio             || '',
    specialties:     listing?.specialties     || '',
    frameworksServed:listing?.frameworksServed|| '',
    regionsServed:   listing?.regionsServed   || '',
    languages:       listing?.languages       || '',
    pricingTier:     listing?.pricingTier     || 'STARTER',
    submit:          false,
  });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [err, setErr]         = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async (submit = false) => {
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const res = await axiosInstance.put('/marketplace/partner/listing', { ...form, submit }, { headers: authHeaders() });
      setMsg(submit ? t('PartnerMarketplace.submittedForReview') : t('PartnerMarketplace.draftSaved'));
      onSaved(res.data);
    } catch (e) {
      setErr(e.response?.data?.message || t('PartnerMarketplace.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const label = (text) => (
    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#374151' }}>{text}</div>
  );

  const inp = (k, ph) => (
    <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 14, boxSizing: 'border-box' }} />
  );

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 24 }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
        {isNew ? t('PartnerMarketplace.createYourListing') : t('PartnerMarketplace.editListing')}
      </div>

      {msg && <div style={{ padding: '8px 12px', background: '#dcfce7', color: '#166534', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{msg}</div>}
      {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{err}</div>}

      {listing?.status && listing.status !== 'DRAFT' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 10,
            background: listing.status === 'APPROVED' ? '#dcfce7' : listing.status === 'REJECTED' ? '#fee2e2' : '#fef9c3',
            color:      listing.status === 'APPROVED' ? '#166534' : listing.status === 'REJECTED' ? '#991b1b' : '#854d0e' }}>
            {listing.status}
          </span>
          {listing.verifiedAt && <span style={{ fontSize: 11, color: '#6b7280' }}>{t('PartnerMarketplace.verifiedOn', { date: listing.verifiedAt?.slice(0, 10) })}</span>}
        </div>
      )}

      {label(t('PartnerMarketplace.bioLabel'))}
      <textarea value={form.bio} onChange={e => set('bio', e.target.value)}
        placeholder={t('PartnerMarketplace.bioPlaceholder')} rows={5}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 14, resize: 'vertical', boxSizing: 'border-box' }} />

      {label(t('PartnerMarketplace.specialtiesLabel'))}
      {inp('specialties', t('PartnerMarketplace.specialtiesPlaceholder'))}

      {label(t('PartnerMarketplace.frameworksServedLabel'))}
      {inp('frameworksServed', t('PartnerMarketplace.frameworksServedPlaceholder'))}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          {label(t('PartnerMarketplace.regionsServedLabel'))}
          {inp('regionsServed', t('PartnerMarketplace.regionsServedPlaceholder'))}
        </div>
        <div>
          {label(t('PartnerMarketplace.languagesLabel'))}
          {inp('languages', t('PartnerMarketplace.languagesPlaceholder'))}
        </div>
      </div>

      {label(t('PartnerMarketplace.pricingTierLabel'))}
      <select value={form.pricingTier} onChange={e => set('pricingTier', e.target.value)}
        style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 20 }}>
        {['STARTER', 'PROFESSIONAL', 'ENTERPRISE'].map(t => <option key={t}>{t}</option>)}
      </select>

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => save(false)} disabled={saving}
          style={{ padding: '8px 18px', background: '#fff', color: '#6366f1', border: '2px solid #6366f1', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
          {saving ? t('PartnerMarketplace.saving') : t('PartnerMarketplace.saveDraft')}
        </button>
        {(!listing?.status || listing.status === 'DRAFT') && (
          <button onClick={() => save(true)} disabled={saving}
            style={{ padding: '8px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {t('PartnerMarketplace.submitForReview')}
          </button>
        )}
      </div>
    </div>
  );
}

function LeadInbox({ leads: initLeads }) {
  const { t } = useTranslation();
  const [leads, setLeads]           = useState(initLeads || []);
  const [selected, setSelected]     = useState(null);
  const [notes, setNotes]           = useState('');
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState('');

  useEffect(() => { setLeads(initLeads || []); }, [initLeads]);

  const openLead = (lead) => {
    setSelected(lead);
    setNotes(lead.partnerNotes || '');
    setMsg('');
  };

  const respond = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axiosInstance.post(`/marketplace/partner/leads/${selected.id}/respond`,
        { partnerNotes: notes }, { headers: authHeaders() });
      setLeads(p => p.map(l => l.id === res.data.id ? res.data : l));
      setSelected(res.data);
      setMsg(t('PartnerMarketplace.responseSaved'));
    } catch { setMsg(t('PartnerMarketplace.saveFailed')); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axiosInstance.patch(`/marketplace/partner/leads/${id}/status`,
        { status }, { headers: authHeaders() });
      setLeads(p => p.map(l => l.id === id ? res.data : l));
      if (selected?.id === id) setSelected(res.data);
    } catch { setMsg(t('PartnerMarketplace.statusUpdateFailed')); }
  };

  const expiredOrSoon = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date(Date.now() + 12 * 3600 * 1000);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16 }}>
      {/* List */}
      <div>
        {leads.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
            <i className="fas fa-inbox" style={{ fontSize: 32, display: 'block', marginBottom: 10, color: '#d1d5db' }} />
            {t('PartnerMarketplace.noLeadsYet')}
          </div>
        ) : (
          leads.map(lead => {
            const b = LEAD_BADGE[lead.status] || LEAD_BADGE.NEW;
            const expired = expiredOrSoon(lead.expiresAt);
            return (
              <div key={lead.id}
                onClick={() => openLead(lead)}
                style={{
                  border: `2px solid ${selected?.id === lead.id ? '#6366f1' : '#e5e7eb'}`,
                  borderRadius: 8, padding: 14, marginBottom: 10, cursor: 'pointer',
                  background: selected?.id === lead.id ? '#f5f3ff' : '#fff',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{lead.prospectName}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{lead.prospectEmail}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, ...b }}>{lead.status}</span>
                    {expired && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>{t('PartnerMarketplace.expiringSoon')}</div>}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  {lead.frameworksNeeded && <span>{lead.frameworksNeeded} · </span>}
                  {lead.timeline && <span>{lead.timeline}</span>}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail */}
      {selected && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{selected.prospectName}</span>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-times" />
            </button>
          </div>
          {msg && <div style={{ padding: '6px 10px', background: '#dbeafe', color: '#1e40af', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{msg}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            {[
              [t('PartnerMarketplace.fieldEmail'), selected.prospectEmail],
              [t('PartnerMarketplace.fieldOrgSize'), selected.orgSize],
              [t('PartnerMarketplace.fieldFrameworks'), selected.frameworksNeeded],
              [t('PartnerMarketplace.fieldTimeline'), selected.timeline],
              [t('PartnerMarketplace.fieldReceived'), selected.createdAt?.slice(0, 10)],
              [t('PartnerMarketplace.fieldExpires'), selected.expiresAt?.slice(0, 16)],
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#f9fafb', borderRadius: 6, padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>{k}</div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{v || '—'}</div>
              </div>
            ))}
          </div>

          {selected.message && (
            <div style={{ background: '#f9fafb', borderRadius: 6, padding: 10, marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{t('PartnerMarketplace.message')}</div>
              <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>{selected.message}</p>
            </div>
          )}

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('PartnerMarketplace.status')}</div>
            <select value={selected.status} onChange={e => updateStatus(selected.id, e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
              {LEAD_STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('PartnerMarketplace.internalNotes')}</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <button onClick={respond} disabled={saving}
            style={{ padding: '7px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
            {saving ? t('PartnerMarketplace.saving') : t('PartnerMarketplace.saveResponse')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function PartnerMarketplace() {
  const { t } = useTranslation();
  const [listing, setListing]   = useState(null);
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState('listing');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, dRes] = await Promise.all([
        axiosInstance.get('/marketplace/partner/listing', { headers: authHeaders() }),
        axiosInstance.get('/marketplace/partner/leads',   { headers: authHeaders() }),
      ]);
      setListing(lRes.data);
      setLeads(dRes.data || []);
    } catch {
      setError(t('PartnerMarketplace.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const s = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1000 },
    h1:   { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
    sub:  { fontSize: 13, color: '#6b7280', marginBottom: 20 },
    tabs: { display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 20 },
    tab:  (a) => ({ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: 13, color: a ? '#6366f1' : '#6b7280',
                    borderBottom: a ? '2px solid #6366f1' : '2px solid transparent', marginBottom: -2 }),
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><i className="fas fa-spinner fa-spin" /></div>;
  if (error)   return <div style={{ padding: 20, color: '#dc2626' }}>{error}</div>;

  const newLeadsCount = leads.filter(l => l.status === 'NEW').length;

  return (
    <div style={s.page}>
      <div style={s.h1}>{t('PartnerMarketplace.partnerPortal')}</div>
      <div style={s.sub}>{t('PartnerMarketplace.partnerPortalSubtitle')}</div>

      <div style={s.tabs}>
        <button style={s.tab(tab === 'listing')} onClick={() => setTab('listing')}>{t('PartnerMarketplace.myListing')}</button>
        <button style={s.tab(tab === 'leads')} onClick={() => setTab('leads')}>
          {t('PartnerMarketplace.leadInbox')}
          {newLeadsCount > 0 && (
            <span style={{ marginLeft: 6, background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700,
                            padding: '1px 6px', borderRadius: 10 }}>
              {newLeadsCount}
            </span>
          )}
        </button>
      </div>

      {tab === 'listing' && (
        <ListingEditor listing={listing} onSaved={setListing} />
      )}

      {tab === 'leads' && (
        <LeadInbox leads={leads} />
      )}
    </div>
  );
}
